// Privacy policy. Required for the App Store listing + good hygiene.
// Plain-language. Family-app context. Honest about what we collect and
// what we don't.

import { Link } from "react-router-dom";
import { Wordmark } from "../components/Brand";

const LAST_UPDATED = "May 17, 2026";

export function Privacy() {
  return (
    <div className="min-h-full bg-bg">
      <header className="border-b border-border bg-surface/80 backdrop-blur sticky top-0 z-10">
        <div className="mx-auto max-w-3xl px-5 h-14 flex items-center justify-between">
          <Link to="/" className="block"><Wordmark size="sm" /></Link>
          <Link to="/" className="text-sm text-text-muted hover:text-text">Back to home</Link>
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-5 py-12 sm:py-16">
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">Privacy Policy</h1>
        <p className="mt-2 text-sm text-text-faint">Last updated: {LAST_UPDATED}</p>

        <Section title="What Carol Care is">
          <p>
            Carol Care is a private, invite-only web app that helps a family coordinate medical visits for a loved one.
            One family member posts a doctor visit, the rest of the family is notified by email and (optionally) SMS,
            one person accepts the visit, and everyone stays in the loop.
          </p>
          <p className="mt-3">
            Carol Care is a coordination tool, not a medical device. It does not provide medical advice, diagnose conditions,
            or store Protected Health Information (PHI) in a HIPAA-compliant manner. Users are responsible for what
            information they choose to enter.
          </p>
        </Section>

        <Section title="What we collect">
          <p>When you use Carol Care, we store the following:</p>
          <ul className="mt-2 list-disc pl-5 space-y-1.5">
            <li><strong>Account info:</strong> your email address, name (optional), preferred timezone, and the role(s) you hold in any care circle.</li>
            <li><strong>Phone number (optional):</strong> only if you opt in to SMS notifications, along with which carrier you use (today).</li>
            <li><strong>Care circle data:</strong> details about your loved one (name, optional date of birth, optional address, free-text notes), the providers you add (doctor names, specialty, facility, address, phone), and visits (date, location, instructions, notes).</li>
            <li><strong>Activity:</strong> who proposed/accepted/declined visits, who posted notes, and a small audit log of security-relevant events (sign-ins, role changes) for ~12 months.</li>
            <li><strong>Auth state:</strong> a hashed, single-use sign-in token (short-lived); a hashed session token (sliding 30-day expiry).</li>
            <li><strong>Hashed IP fingerprint</strong> on auth attempts (used only for rate-limiting; the raw IP is never stored).</li>
          </ul>
          <p className="mt-3">
            We do <strong>not</strong> collect: passwords (we use email magic-link / one-time code only), payment info (the app is free), browsing data outside our domain, advertising IDs, or analytics on individual user behavior.
          </p>
        </Section>

        <Section title="How we use it">
          <ul className="mt-2 list-disc pl-5 space-y-1.5">
            <li>To run the app — show you the right circles, send notifications about the visits you're a member of.</li>
            <li>To send transactional emails: sign-in links/codes, visit notifications, invites, support replies.</li>
            <li>To send opt-in SMS for the events you've enabled (master switch off by default).</li>
            <li>To enforce who can see and act on each circle (membership-based access).</li>
          </ul>
          <p className="mt-3">
            We do not sell or share your data with advertisers, brokers, or marketers. Carol Care has no advertising.
          </p>
        </Section>

        <Section title="Third-party services we use">
          <ul className="mt-2 list-disc pl-5 space-y-1.5">
            <li><strong>Cloudflare</strong> — hosts the app and database. Standard infrastructure provider; sees the same data we store.</li>
            <li><strong>Resend</strong> — sends transactional email. Receives the recipient's email address and the message contents.</li>
            <li><strong>Twilio</strong> (if SMS is enabled and we've switched providers from carrier gateway to Twilio) — sends SMS. Receives your phone number and the message text.</li>
            <li><strong>Bunny Fonts</strong> — serves the Inter font. Privacy-respecting (no Google), no per-user tracking.</li>
          </ul>
          <p className="mt-3">
            Each provider operates under their own privacy policy. We have selected providers known for not selling user data.
          </p>
        </Section>

        <Section title="Who can see your data">
          <ul className="mt-2 list-disc pl-5 space-y-1.5">
            <li><strong>You</strong> — everything you've entered about yourself and the circle(s) you're in.</li>
            <li><strong>Members of your care circle</strong> — visits, providers, loved-one details, and members of the same circle.</li>
            <li><strong>Members of OTHER care circles</strong> — see nothing. Every API endpoint is gated by circle membership.</li>
            <li><strong>The platform operator</strong> (currently the developer, John Kahrhoff) — has the technical ability to query the database for support and operational purposes. Does not view circle data unless investigating a reported issue.</li>
          </ul>
        </Section>

        <Section title="Retention">
          <p>
            We keep your data for as long as your account is active. If you ask us to delete your account, we'll
            remove your user record and your memberships, which removes you from any circle. Visits you created
            remain in the circle history with your name (so the family record is preserved) unless you specifically
            request those be removed too.
          </p>
          <p className="mt-3">
            Audit log entries retain for approximately 12 months. Hashed IPs and rate-limit records retain for under 24 hours.
          </p>
        </Section>

        <Section title="Your rights">
          <p>You can request any of the following at any time by emailing <a href="mailto:support@carolcare.io" className="text-primary hover:underline">support@carolcare.io</a>:</p>
          <ul className="mt-2 list-disc pl-5 space-y-1.5">
            <li>Access — a copy of all data we have about you.</li>
            <li>Correction — edits to anything we have wrong.</li>
            <li>Deletion — remove your account and personal information.</li>
            <li>Portability — receive your data in a machine-readable format (JSON).</li>
            <li>Opt out of any optional channel (SMS) at any time via your in-app Notifications settings.</li>
          </ul>
        </Section>

        <Section title="Children">
          <p>
            Carol Care is intended for adults coordinating care for an adult loved one. It is not designed for or directed at children under 13. We do not knowingly collect information from anyone under 13. If you believe a child has provided information through the app, contact us and we'll remove it.
          </p>
        </Section>

        <Section title="Security">
          <p>We take reasonable steps to protect the data you entrust to us:</p>
          <ul className="mt-2 list-disc pl-5 space-y-1.5">
            <li>HTTPS everywhere — all traffic between your device and Carol Care is encrypted in transit.</li>
            <li>No passwords — sign-in uses single-use magic links or 6-digit codes that expire in 15 minutes.</li>
            <li>Sessions are HttpOnly cookies, server-side revocable, with a sliding 30-day expiry.</li>
            <li>Audit log captures security-relevant events.</li>
            <li>Rate limits on auth endpoints to deter brute-force.</li>
          </ul>
          <p className="mt-3">
            No system is perfectly secure. If you suspect a security issue, email <a href="mailto:support@carolcare.io" className="text-primary hover:underline">support@carolcare.io</a> as soon as you can.
          </p>
        </Section>

        <Section title="Changes to this policy">
          <p>
            If we change this policy in a way that affects how we handle your data, we'll update the "Last updated" date above and, for material changes, notify circle admins by email.
          </p>
        </Section>

        <Section title="Contact">
          <p>
            Questions or requests: <a href="mailto:support@carolcare.io" className="text-primary hover:underline">support@carolcare.io</a>. Replies usually within a day or two.
          </p>
        </Section>

        <footer className="mt-12 pt-8 border-t border-border text-sm text-text-muted flex items-center justify-between gap-3">
          <Wordmark size="sm" />
          <div className="flex items-center gap-5">
            <Link to="/terms" className="hover:text-text">Terms</Link>
            <Link to="/" className="hover:text-text">Back to home</Link>
          </div>
        </footer>
      </main>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-10">
      <h2 className="text-xl font-semibold tracking-tight">{title}</h2>
      <div className="mt-3 text-[15px] leading-relaxed text-text-muted [&_p]:text-text-muted [&_strong]:text-text">{children}</div>
    </section>
  );
}
